/**
 * Announcer manifest loader (DESIGN-client §8, spec §4.3).
 *
 * `packages/server/scripts/announcer-generate.ts` writes
 * `public/assets/announcer/<voiceId>/manifest.json` plus one mp3 per spoken line. This module
 * fetches that manifest at boot, turns it into `{ id, url }` registrations, and hands them to the
 * audio engine. No manifest (the common case before anyone has run the generator) is not an
 * error: the Announcer keeps playing subtitle-only, exactly as it does today.
 *
 * ## Clip ids
 *
 * The generator expands `{team}` and `{n}` into concrete lines, so a real voice pack has ~171
 * clips across 12 events rather than one per event. `announcerClipId` extends the old
 * `${eventKey}:${voiceId}` key with the variant and the fills, and still starts with it.
 *
 * Registration goes to `audio/announcerClips.ts`, which owns the fetch/decode into the engine and
 * the runtime lookup. That module is loaded with a dynamic `import()` on purpose: this file sits
 * in the entry chunk, and a static import would pull `AudioEngine` onto the boot path for the
 * common case where no voice pack is installed at all (C-T25).
 *
 * Note on selection: at runtime the `announcerBark` cue carries only the rendered subtitle, since
 * the server renders the template and discards the variant and fills, so `announcerClips.ts`
 * matches on that text. `pickAnnouncerClip` below stays the manifest-level rule for callers that
 * do know the variant and fills.
 */
import { netBase } from '../bridge/embedded';
import { store } from '../store/store';

/** One generated line. Mirrors `ManifestClip` in the generator; kept structural on purpose. */
export interface AnnouncerManifestClip {
  file: string;
  text: string;
  variant: number;
  vars: { team?: string | number; n?: string | number };
}

export interface AnnouncerManifest {
  voiceId: string;
  model?: string;
  generatedAt?: string;
  clips: Record<string, AnnouncerManifestClip[]>;
  /** Variants that use `{name}` and therefore have no audio; subtitle only. */
  subtitleOnly?: Array<{ eventKey: string; variant: number; text: string }>;
}

export interface AnnouncerClipRegistration {
  id: string;
  url: string;
  eventKey: string;
  variant: number;
  vars: AnnouncerManifestClip['vars'];
  text: string;
}

export type ClipVars = AnnouncerManifestClip['vars'];

/** Directory the generator writes into, behind the Discord `/.proxy` prefix when embedded. */
export function announcerBase(voiceId: string, base: string = netBase()): string {
  return `${base}/assets/announcer/${encodeURIComponent(voiceId)}`;
}

/** `event:voice#variant` plus the fills that distinguish this line from its siblings. */
export function announcerClipId(eventKey: string, voiceId: string, variant: number, vars: ClipVars = {}): string {
  let id = `${eventKey}:${voiceId}#${variant}`;
  if (vars.team !== undefined) id += `|team=${vars.team}`;
  if (vars.n !== undefined) id += `|n=${vars.n}`;
  return id;
}

function isManifest(value: unknown): value is AnnouncerManifest {
  if (!value || typeof value !== 'object') return false;
  const m = value as Partial<AnnouncerManifest>;
  return typeof m.voiceId === 'string' && !!m.clips && typeof m.clips === 'object';
}

/**
 * Fetch the manifest for `voiceId`. Returns null for "no voice pack installed" — a 404, a parse
 * failure and an offline fetch are all the same non-event to the caller.
 */
export async function fetchAnnouncerManifest(
  voiceId: string,
  fetchFn: typeof fetch = fetch,
  base?: string,
): Promise<AnnouncerManifest | null> {
  if (!voiceId) return null;
  try {
    const res = await fetchFn(`${announcerBase(voiceId, base)}/manifest.json`);
    if (!res.ok) return null;
    const body: unknown = await res.json();
    return isManifest(body) ? body : null;
  } catch {
    return null;
  }
}

/** Flatten a manifest into the `{ id, url }` pairs the audio engine loads. */
export function toClipRegistrations(manifest: AnnouncerManifest, base?: string): AnnouncerClipRegistration[] {
  const dir = announcerBase(manifest.voiceId, base);
  const out: AnnouncerClipRegistration[] = [];
  for (const [eventKey, clips] of Object.entries(manifest.clips)) {
    for (const clip of clips ?? []) {
      out.push({
        id: announcerClipId(eventKey, manifest.voiceId, clip.variant, clip.vars),
        url: `${dir}/${clip.file}`,
        eventKey,
        variant: clip.variant,
        vars: clip.vars ?? {},
        text: clip.text,
      });
    }
  }
  return out;
}

const sameVar = (a: string | number | undefined, b: string | number | undefined): boolean =>
  a === undefined || b === undefined ? a === b : String(a) === String(b);

/**
 * The clip for one bark, or null when the line is subtitle-only.
 *
 * `index` is the cue seq, matching `subtitleFor` in `@spades/shared`: the same integer picks the
 * same variant, so the spoken line and the on-screen subtitle always agree. Candidates are first
 * narrowed to the clips whose `{team}`/`{n}` fills match what the cue carries.
 */
export function pickAnnouncerClip(
  manifest: AnnouncerManifest | null,
  eventKey: string,
  index: number,
  vars: ClipVars = {},
): AnnouncerManifestClip | null {
  const all = manifest?.clips?.[eventKey];
  if (!all || all.length === 0) return null;
  const matching = all.filter((c) => sameVar(c.vars?.team, vars.team) && sameVar(c.vars?.n, vars.n));
  if (matching.length === 0) return null;
  const variants = [...new Set(matching.map((c) => c.variant))].sort((a, b) => a - b);
  const variant = variants[((index % variants.length) + variants.length) % variants.length]!;
  return matching.find((c) => c.variant === variant) ?? null;
}

/**
 * Hand the pack to the audio engine. Dynamically imported so `AudioEngine` stays off the boot
 * path; `window.__spades.audio.registerClips` exists only under `?e2e=1`, so it cannot be the
 * production route.
 */
async function registerWithAudio(clips: AnnouncerClipRegistration[]): Promise<void> {
  const { registerAnnouncerClips } = await import('../audio/announcerClips');
  await registerAnnouncerClips(clips).loading;
}

/**
 * The voice ids the generator has published, from `announcer/index.json`. The client never knows
 * the ElevenLabs voice id up front, so the pack announces itself.
 */
export async function fetchAnnouncerVoices(fetchFn: typeof fetch = fetch, base: string = netBase()): Promise<string[]> {
  try {
    const res = await fetchFn(`${base}/assets/announcer/index.json`);
    if (!res.ok) return [];
    const body: unknown = await res.json();
    const voices = (body as { voices?: unknown } | null)?.voices;
    return Array.isArray(voices) ? voices.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export interface InstallOptions {
  fetchFn?: typeof fetch;
  base?: string;
  /** Injected in tests; defaults to the audio agent's hook. */
  register?: (clips: AnnouncerClipRegistration[]) => void | Promise<void>;
}

/**
 * Load the voice pack and register it. Resolves to the manifest, or null when there is none.
 * Safe to call unconditionally at boot: it never throws and never blocks the table.
 */
export async function installAnnouncerVoice(voiceId: string, opts: InstallOptions = {}): Promise<AnnouncerManifest | null> {
  const manifest = await fetchAnnouncerManifest(voiceId, opts.fetchFn ?? fetch, opts.base);
  if (!manifest) return null;

  const register = opts.register ?? registerWithAudio;
  try {
    await register(toClipRegistrations(manifest, opts.base));
  } catch (err) {
    // A voice pack that fails to load costs subtitles-only audio, never the game.
    console.warn('[announcer] clip registration failed', err);
    return null;
  }

  store.getState().setPresentation({ announcerVoiceId: manifest.voiceId });
  return manifest;
}

/**
 * Boot hook: discover the installed voice pack and register it. No pack is the normal state
 * before anyone has run the generator, and costs one 404. Never throws.
 */
export async function installDefaultAnnouncerVoice(opts: InstallOptions = {}): Promise<AnnouncerManifest | null> {
  const voices = await fetchAnnouncerVoices(opts.fetchFn ?? fetch, opts.base);
  const voiceId = voices[0];
  return voiceId ? installAnnouncerVoice(voiceId, opts) : null;
}
